// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck

/**
 https://github.com/datalog/datamatrix-svg
 under MIT license
 # datamatrix.js has no dependencies
 Copyright (c) 2020 Constantine
 */

function isHex(c: string) {
  return /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(c);
}

const bit = function (M: Array<Array<boolean>>, x: number, y: number) {
  M[y] = M[y] ?? [];

  M[y][x] = true;
};
const toAscii = function (t: string) {
  const r: Array<number> = [];
  const l = t.length;
  for (let i = 0; i < l; i++) {
    const c = t.charCodeAt(i);
    const c1 = i + 1 < l ? t.charCodeAt(i + 1) : 0;
    if (c > 47 && c < 58 && c1 > 47 && c1 < 58) {
      /* 2 digits */
      r.push((c - 48) * 10 + c1 + 82) /* - 48 + 130 = 82 */;
      i++;
    } else if (c > 127) {
      /* extended char */
      r.push(235);
      r.push((c - 127) & 255);
    } else r.push(c + 1); /* char */
  }
  return r;
};
const toBase = function (t: string) {
  const r = [231]; /* switch to Base 256 */
  const l = t.length;

  if (l > 250) {
    r.push((37 + ((l / 250) | 0)) & 255); /* length high byte (in 255 state algo) */
  }

  r.push(
    ((l % 250) + ((149 * (r.length + 1)) % 255) + 1) & 255,
  ); /* length low byte (in 255 state algo) */

  for (let i = 0; i < l; i++) {
    r.push(
      (t.charCodeAt(i) + ((149 * (r.length + 1)) % 255) + 1) & 255,
    ); /* data in 255 state algo */
  }

  return r;
};
const toEdifact = function (t: string) {
  const n = t.length;
  const l = (n + 1) & -4;
  let cw = 0;
  let ch;
  const r = l > 0 ? [240] : []; /* switch to Edifact */

  for (let i = 0; i < l; i++) {
    if (i < l - 1) {
      /* encode char */
      ch = t.charCodeAt(i);
      if (ch < 32 || ch > 94) {
        return []; /* not in set */
      }
    } else {
      ch = 31; /* return to ASCII */
    }

    cw = cw * 64 + (ch & 63);

    if ((i & 3) === 3) {
      /* 4 data in 3 words */
      r.push(cw >> 16);
      r.push((cw >> 8) & 255);
      r.push(cw & 255);
      cw = 0;
    }
  }

  return l > n ? r : r.concat(toAscii(t.substr(l === 0 ? 0 : l - 1))); /* last chars */
};

const toText = function (t: string, s: number[]) {
  let i;
  let j;
  let cc = 0;
  let cw = 0;
  const l = t.length;
  const r = [s[0]]; /* start switch */
  const push = function (v: number) {
    /* pack 3 chars in 2 codes */
    cw = 40 * cw + v;

    /* add code */
    if (cc++ === 2) {
      r.push(++cw >> 8);
      r.push(cw & 255);
      cc = 0;
      cw = 0;
    }
  };

  for (i = 0; i < l; i++) {
    /* last char in ASCII is shorter */
    if (cc === 0 && i === l - 1) break;

    let ch = t.charCodeAt(i);

    if (ch > 127 && r[0] !== 238) {
      /* extended char */
      push(1);
      push(30);
      ch -= 128; /* hi bit in C40 & TEXT */
    }

    // eslint-disable-next-line no-empty
    for (j = 1; ch > s[j]; j += 3) {} /* select char set */

    const x = s[j + 1]; /* shift */

    if (x === 8 || (x === 9 && cc === 0 && i === l - 1))
      return []; /* char not in set or padding fails */

    if (x < 5 && cc === 2 && i === l - 1) {
      break; /* last char in ASCII */
    }
    if (x < 5) {
      push(x); /* shift */
    }

    push(ch - s[j + 2]); /* char offset */
  }

  if (cc === 2 && r[0] !== 238) {
    /* add pad */

    push(0);
  }

  r.push(254); /* return to ASCII */

  if (cc > 0 || i < l) {
    return r.concat(toAscii(t.substr(i - cc))); /* last chars */
  }

  return r;
};

const encodeMsg = function (text: string, rct: 0 | 1) {
  const unescapedText = unescape(encodeURI(text));
  const M: Array<Array<boolean>> = [];

  let enc = toAscii(unescapedText);
  let el = enc.length;
  let k = toText(
    unescapedText,
    [
      /* C40 */ 230, 31, 0, 0, 32, 9, 29, 47, 1, 33, 57, 9, 44, 64, 1, 43, 90, 9, 51, 95, 1, 69,
      127, 2, 96, 255, 1, 0,
    ],
  );
  let l = k.length;
  if (l > 0 && l < el) {
    enc = k;
    el = l;
  }

  k = toText(
    unescapedText,
    [
      /* TEXT */ 239, 31, 0, 0, 32, 9, 29, 47, 1, 33, 57, 9, 44, 64, 1, 43, 90, 2, 64, 95, 1, 69,
      122, 9, 83, 127, 2, 96, 255, 1, 0,
    ],
  );
  l = k.length;
  if (l > 0 && l < el) {
    enc = k;
    el = l;
  }

  k = toText(
    unescapedText,
    [
      /* X12 */ 238, 12, 8, 0, 13, 9, 13, 31, 8, 0, 32, 9, 29, 41, 8, 0, 42, 9, 41, 47, 8, 0, 57, 9,
      44, 64, 8, 0, 90, 9, 51, 255, 8, 0,
    ],
  );
  l = k.length;
  if (l > 0 && l < el) {
    enc = k;
    el = l;
  }

  k = toEdifact(unescapedText);
  l = k.length;
  if (l > 0 && l < el) {
    enc = k;
    el = l;
  }

  k = toBase(unescapedText);
  l = k.length;
  if (l > 0 && l < el) {
    enc = k;
    el = l;
  }

  let h;
  let w;
  let nc = 1;
  let nr = 1;
  let j = -1;
  let s;
  let b: number | number[] = 1; /* compute symbol size */
  const rs = new Array(70); /* reed / solomon code */
  const rc = new Array(70);
  const lg = new Array<number>(256); /* log / exp table for multiplication */
  const ex = new Array(255);

  if (rct === 1 && el < 50) {
    /* rect */

    k = [/* symbol width, checkwords */ 16, 7, 28, 11, 24, 14, 32, 18, 32, 24, 44, 28];

    do {
      w = k[++j]; /* width */
      h = 6 + (j & 12); /* height */
      l = (w * h) / 8; /* bytes count in symbol */
    } while (l - k[++j] < el); /* could we fill the rect? */

    /* column regions */
    if (w > 25) {
      nc = 2;
    }
  } else {
    /* square */

    w = 6;
    h = 6;
    k = [
      5, 7, 10, 12, 14, 18, 20, 24, 28, 36, 42, 48, 56, 68, 84, 112, 144, 192, 224, 272, 336, 408,
      496, 620,
    ]; /* rs checkwords */

    let i = 2; /* size increment */
    do {
      if (++j === k.length) {
        return { M, xx: 0, yy: 0 };
        /* msg is too long */
      }

      if (w > 11 * i) {
        i = (4 + i) & 12; /* advance increment */
      }

      w += i;
      h = w;
      l = (w * h) >> 3;
    } while (l - k[j] < el);

    if (w > 27) {
      nc = 2 * ((w / 54) | 0) + 2; /* regions */
      nr = nc;
    }
    if (l > 255) {
      b = 2 * (l >> 9) + 2; /* blocks */
    }
  }

  s = k[j]; /* rs checkwords */
  const fw = w / nc; /* region size */
  const fh = h / nr; /* symbol size, regions, region size */

  /* first padding */
  if (el < l - s) {
    enc[el++] = 129;
  }

  /* more padding */
  while (el < l - s) {
    enc[el++] = (((149 * el) % 253) + 130) % 254;
  }

  /* Reed Solomon error detection and correction */
  s /= b;

  /* log / exp table of Galois field */
  for (let ii = 1, i = 0; i < 255; i++) {
    ex[i] = ii;
    lg[ii] = i;
    ii += ii;

    if (ii > 255) {
      ii ^= 301; /* 301 == a^8 + a^5 + a^3 + a^2 + 1 */
    }
  }

  /* RS generator polynomial */
  rs[s] = 0;
  for (let i = 1; i <= s; i++) {
    for (j = s - i, rs[j] = 1; j < s; j++) {
      rs[j] = rs[j + 1] ^ ex[(lg[rs[j]] + i) % 255];
    }
  }

  /* RS correction data for each block */
  for (let c = 0; c < b; c++) {
    for (let i = 0; i <= s; i++) {
      rc[i] = 0;
    }
    for (let i = c; i < el; i += b) {
      for (let ii = 0, x = rc[0] ^ enc[i]; ii < s; ii++) {
        rc[ii] = rc[ii + 1] ^ (x ? ex[(lg[rs[ii]] + lg[x]) % 255] : 0);
      }
    }
    /* interleaved correction data */
    for (let i = 0; i < s; i++) {
      enc[el + c + i * b] = rc[i];
    }
  }

  /* layout perimeter finder pattern */
  /* horizontal */
  for (let i = 0; i < h + 2 * nr; i += fh + 2) {
    for (let ii = 0; ii < w + 2 * nc; ii++) {
      bit(M, ii, i + fh + 1);
      if ((ii & 1) === 0) {
        bit(M, ii, i);
      }
    }
  }

  /* vertical */
  for (let i = 0; i < w + 2 * nc; i += fw + 2) {
    for (let ii = 0; ii < h; ii++) {
      bit(M, i, ii + ((ii / fh) | 0) * 2 + 1);
      if ((ii & 1) === 1) {
        bit(M, i + fw + 1, ii + ((ii / fh) | 0) * 2);
      }
    }
  }

  s = 2; /* step */
  let c = 0; /* column */
  let r = 4; /* row */
  b = [/* nominal byte layout */ 0, 0, -1, 0, -2, 0, 0, -1, -1, -1, -2, -1, -1, -2, -2, -2];

  /* diagonal steps */
  for (let i = 0; i < l; r -= s, c += s) {
    if (r === h - 3 && c === -1) {
      k = [
        /* corner A layout */ w,
        6 - h,
        w,
        5 - h,
        w,
        4 - h,
        w,
        3 - h,
        w - 1,
        3 - h,
        3,
        2,
        2,
        2,
        1,
        2,
      ];
    } else if (r === h + 1 && c === 1 && (w & 7) === 0 && (h & 7) === 6) {
      k = [
        /* corner D layout */ w - 2,
        -h,
        w - 3,
        -h,
        w - 4,
        -h,
        w - 2,
        -1 - h,
        w - 3,
        -1 - h,
        w - 4,
        -1 - h,
        w - 2,
        -2,
        -1,
        -2,
      ];
    } else {
      if (r === 0 && c === w - 2 && w & 3) {
        /* corner B: omit upper left */

        continue;
      }
      if (r < 0 || c >= w || r >= h || c < 0) {
        /* outside */
        s = -s; /* turn around */
        r += 2 + s / 2;
        c += 2 - s / 2;

        while (r < 0 || c >= w || r >= h || c < 0) {
          r -= s;
          c += s;
        }
      }
      if (r === h - 2 && c === 0 && w & 3) {
        k = [
          /* corner B layout */ w - 1,
          3 - h,
          w - 1,
          2 - h,
          w - 2,
          2 - h,
          w - 3,
          2 - h,
          w - 4,
          2 - h,
          0,
          1,
          0,
          0,
          0,
          -1,
        ];
      } else if (r === h - 2 && c === 0 && (w & 7) === 4) {
        k = [
          /* corner C layout */ w - 1,
          5 - h,
          w - 1,
          4 - h,
          w - 1,
          3 - h,
          w - 1,
          2 - h,
          w - 2,
          2 - h,
          0,
          1,
          0,
          0,
          0,
          -1,
        ];
      } else if (r === 1 && c === w - 1 && (w & 7) === 0 && (h & 7) === 6) {
        continue;
        /* omit corner D */
      } else {
        k = b;
        /* nominal L - shape layout */
      }
    }

    /* layout each bit */
    for (el = enc[i++], j = 0; el > 0; j += 2, el >>= 1) {
      if (el & 1) {
        let x = c + k[j];
        let y = r + k[j + 1];

        /* wrap around */
        if (x < 0) {
          x += w;
          y += 4 - ((w + 4) & 7);
        }
        if (y < 0) {
          y += h;
          x += 4 - ((h + 4) & 7);
        }

        /* region gap */
        bit(M, x + 2 * ((x / fw) | 0) + 1, y + 2 * ((y / fh) | 0) + 1);
      }
    }
  }

  /* unfilled corner */
  for (let i = w; i & 3; i--) {
    bit(M, i, i);
  }

  const xx = w + 2 * nc;
  const yy = h + 2 * nr;
  return { M, xx, yy };
};

const ns = 'http://www.w3.org/2000/svg';

function svg(
  n: 'path' | 'svg',
  a: {
    viewBox?: string;
    width?: number;
    height?: number;
    fill?: string;
    'shape-rendering'?: string;
    xmlns?: string;
    version?: string;
    d?: string;
    transform?: string;
  },
) {
  const svgElement = document.createElementNS(ns, n);

  Object.entries(a).forEach(([o, v]) => {
    if (v != null) svgElement.setAttribute(o, v.toString());
  });

  return svgElement;
}

export function DATAMatrix({
  msg,
  dim = 256,
  rct = 0,
  pad = 2,
  pal = ['#000'],
  verbose = false,
}: {
  msg: string;
  dim?: number;
  rct?: 0 | 1;
  pad?: number;
  pal?: [string] | [string, string];
  verbose?: boolean;
}) {
  const p = pal;
  const mx = [1, 0, 0, 1, pad, pad];

  const { M, xx, yy } = encodeMsg(msg, rct);

  const sx = xx + pad * 2;
  const sy = yy + pad * 2;

  let y = yy;

  let path = '';
  while (y--) {
    let d = 0;
    let x = xx;

    while (x--) {
      if (M[y][x]) {
        if (!verbose) {
          d++;

          if (!M[y][x - 1]) {
            path += `M${x},${y}h${d}v1h-${d}v-1z`;
            d = 0;
          }
        } else path += `M${x},${y}h1v1h-1v-1z`;
      }
    }
  }

  const fg = isHex(p[0]) ? p[0] : '#000';
  const r = svg('svg', {
    viewBox: [0, 0, sx, sy].join(' '),
    width: ((dim / sy) * sx) | 0,
    height: dim,
    fill: fg,
    'shape-rendering': 'crispEdges',
    xmlns: ns,
    version: '1.1',
  });

  const bg = p.length === 2 && isHex(p[1]) ? p[1] : undefined;
  if (bg != null)
    r.appendChild(
      svg('path', {
        fill: bg,
        d: `M0,0v${sy}h${sx}V0H0Z`,
      }),
    );

  r.appendChild(
    svg('path', {
      transform: `matrix(${mx.join(' ')})`,
      d: path,
    }),
  );

  return r;
}
